<script setup lang="ts">
import { ref, computed } from "vue";

const props = withDefaults(
  defineProps<{
    productNumber: string;
    productGroup: string | undefined;
    isExpanded?: boolean;
  }>(),
  {
    isExpanded: false,
  },
);
const { productNumber, productGroup } = toRefs(props);
const { locale } = useI18n();

const faqData = ref<any[]>([]);

try {
  if (!productNumber.value || !productGroup.value) {
    throw new Error("Fehlende Produktdaten für API-Aufruf");
  }

  const response = await $fetch(
    `/api/faq/${locale.value}/${getCoreProductNumber(productNumber.value)}`,
    {
      query: {
        prodcateg: productGroup.value,
      },
    },
  );

  if (!response) {
    throw new Error("Error occured on fetching FAQ data");
  }

  faqData.value = response.map((item: any) => ({
    ...item,
    field_fqt_text: fixYouTubeIframesInHtml(item.field_fqt_text),
  }));
} catch (error) {
  console.error("Error: Fetching product from getProductByProdNumber:", error);
}

const isShowAll = ref(false);
const searchTerm = ref("");
const filteredFAQs = computed(() => {
  const term = searchTerm.value.toLowerCase();
  return faqData.value.filter(
    (item) =>
      item.question.toLowerCase().includes(term) ||
      item.field_fqt_text.toLowerCase().includes(term),
  );
});

// Function to fix YouTube iframe URLs
function fixYouTubeIframesInHtml(htmlStr: string) {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlStr;

  const iframes = tempDiv.querySelectorAll("iframe");

  iframes.forEach((iframe) => {
    const src = iframe.getAttribute("src");
    if (src && src.includes("youtube.com")) {
      const urlParams = new URLSearchParams(src.split("?")[1]);
      const videoUrl = urlParams.get("url");

      if (videoUrl && videoUrl.includes("youtube.com")) {
        const videoId = new URL(videoUrl).searchParams.get("v");
        const newSrc = `https://www.youtube.com/embed/${videoId}`;
        iframe.setAttribute("src", newSrc);
      }
    }
  });

  return tempDiv.innerHTML;
}
</script>

<template>
  <div>
    <h2 class="c-scheppach-primary-500 text-[32px]">
      {{ $t("product.faq") }}
    </h2>
    <p class="mb-5 text-4.2 c-scheppach-primary-500 w-auto sm:w-125">
      {{ $t("form.contactForm.additionalFaqText") }}
    </p>
    <div
      class="flex items-center h-10 gap-sm px-sm bg-scheppach-primary-50 b-scheppach-primary-700 border-b mt-5 xl:mt-0 mb-5 w-full lg:w-500px"
    >
      <input
        v-model="searchTerm"
        type="search"
        aria-label="Search for products"
        class="grow font-700 focus:outline-none h-full bg-transparent"
        :placeholder="$t('form.searchPlaceholder')"
      />
      <div
        class="i-sl-search-alternate flex-none size-5 xl:size-6 cursor-pointer"
      ></div>
    </div>
    <div v-if="filteredFAQs.length > 0">
      <ClientOnly>
        <SharedDetailsSummary
          v-for="(item, index) in isShowAll || searchTerm
            ? filteredFAQs
            : filteredFAQs.slice(0, 6)"
          :key="index"
          :title="item.question"
          :open="false"
          :animation="true"
          class="mb-2"
        >
          <p
            class="bg-white p-4 style-for-inline-v-html"
            v-html="item.field_fqt_text"
          ></p>
        </SharedDetailsSummary>
      </ClientOnly>

      <div class="text-center">
        <button
          class="mt-5 rd w-full sm:w-300px py-2 text-[16px] bg-white hover:bg-scheppach-primary-50 b b-scheppach-primary-700 c-scheppach-primary-700"
          @click="isShowAll = !isShowAll"
        >
          {{ !isShowAll ? $t("product.showAll") : $t("product.showLess") }}
        </button>
      </div>
    </div>
    <div v-else class="flex items-center mt-5 text-6 c-scheppach-primary-500">
      {{ $t("product.noFaq") }}
    </div>
  </div>
</template>
